import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { cn } from "../lib/utils";
import { Globe, User, Users, Settings, LogIn, Plus, ChevronDown, BarChart3, UserCircle, TrendingUp, Shield, Bug, Bell, UserPlus, Sword, Trash2, X, Target, Trophy, Layout } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import BugReportModal from "./BugReportModal";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const TrackerTabs = ({
  trackerType,
  selectedGuildId,
  guilds,
  user,
  onTrackerTypeChange,
  onManageGuilds,
  onLogin,
  compactMode,
  onToggleCompact,
}) => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const selectedGuild = guilds.find((g) => g.id === selectedGuildId);
  const [showBugReport, setShowBugReport] = useState(false);
  const [notifications, setNotifications] = useState({ total: 0, pending_invitations: 0, pending_applications: 0, pending_friend_requests: 0 });

  // Fetch notifications periodically
  useEffect(() => {
    if (!user) {
      setNotifications({ total: 0, pending_invitations: 0, pending_applications: 0 });
      return;
    }

    const fetchNotifications = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/notifications`);
        setNotifications(response.data);
      } catch (error) {
        console.error("Failed to fetch notifications", error);
      }
    };

    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, [user]);

  // Clear all Hunter's Chance notifications
  const clearAllHunterChanceNotifications = async () => {
    try {
      await axios.delete(`${API_URL}/api/notifications/hunter-chance`);
      setNotifications(prev => ({
        ...prev,
        hunter_chance_notifications: [],
        hunter_chance_count: 0,
        total: prev.total - (prev.hunter_chance_notifications?.length || 0)
      }));
    } catch (error) {
      console.error("Failed to clear notifications", error);
    }
  };

  // Clear a single notification
  const clearSingleNotification = async (notifId, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API_URL}/api/fight/notifications/${notifId}`);
      setNotifications(prev => ({
        ...prev,
        hunter_chance_notifications: prev.hunter_chance_notifications?.filter(n => n.id !== notifId) || [],
        hunter_chance_count: Math.max(0, prev.hunter_chance_count - 1),
        total: Math.max(0, prev.total - 1)
      }));
    } catch (error) {
      console.error("Failed to clear notification", error);
    }
  };

  // Get description for notification type
  const getNotificationDescription = (notif) => {
    const type = notif.type;
    const username = notif.username || "Someone";
    const bossName = notif.boss_name || "boss";
    
    switch(type) {
      case "bounty_accepted":
        return `${username} accepted your bounty for ${bossName}`;
      case "bounty_confirm_pending":
        return `${username} completed bounty - confirm for ${bossName}`;
      case "bounty_completed":
        return `Bounty completed for ${bossName}!`;
      case "expedition_application":
        return `${username} applied to join your ${bossName} expedition`;
      case "expedition_client_application":
        return `${username} wants to be buyer for ${bossName}`;
      case "expedition_accepted":
        return `You were accepted to ${bossName} expedition by ${username}`;
      case "expedition_rejected":
        return `Your ${bossName} application was rejected`;
      case "expedition_client_selected":
        return `You were selected as buyer for ${bossName}`;
      case "expedition_client_confirmed":
        return `Buyer confirmed for ${bossName} expedition`;
      case "expedition_invite":
        return `${username} invited you to ${bossName} expedition`;
      case "expedition_expelled":
        return `You were removed from ${bossName} expedition`;
      case "admin_bounty_deleted":
        return `Admin deleted bounty for ${bossName}`;
      case "admin_expedition_deleted":
        return `Admin deleted ${bossName} expedition`;
      default:
        return `Update for ${bossName}`;
    }
  };

  return (
    <div className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50" data-testid="tracker-tabs">
      <div className="flex items-center justify-between px-4 py-2">
        {/* Tracker Type Tabs */}
        <div className="flex items-center gap-2">
          <Button
            variant={trackerType === "global" ? "default" : "ghost"}
            size="sm"
            onClick={() => onTrackerTypeChange("global")}
            className={cn(
              "gap-2",
              trackerType === "global" && "bg-primary text-primary-foreground"
            )}
            data-testid="global-tracker-tab"
          >
            <Globe className="w-4 h-4" />
            <span className="hidden sm:inline">Global</span>
          </Button>

          <Button
            variant={trackerType === "personal" ? "default" : "ghost"}
            size="sm"
            onClick={() => onTrackerTypeChange("personal")}
            className={cn(
              "gap-2",
              trackerType === "personal" && "bg-primary text-primary-foreground"
            )}
            data-testid="personal-tracker-tab"
          >
            <User className="w-4 h-4" />
            <span className="hidden sm:inline">Personal</span>
          </Button>

          {/* Guild Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant={trackerType === "guild" ? "default" : "ghost"}
                size="sm"
                className={cn(
                  "gap-2",
                  trackerType === "guild" && "bg-primary text-primary-foreground"
                )}
                data-testid="guild-tracker-tab"
              >
                <Users className="w-4 h-4" />
                <span className="hidden sm:inline">
                  {trackerType === "guild" && selectedGuild ? selectedGuild.name : "Guild"}
                </span>
                <ChevronDown className="w-3 h-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              <DropdownMenuLabel>Your Guilds</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {guilds.length === 0 ? (
                <DropdownMenuItem disabled className="text-muted-foreground">
                  No guilds yet
                </DropdownMenuItem>
              ) : (
                guilds.map((guild) => (
                  <DropdownMenuItem
                    key={guild.id}
                    onClick={() => onTrackerTypeChange("guild", guild.id)}
                    className={cn(
                      selectedGuildId === guild.id && "bg-primary/10"
                    )}
                  >
                    <Users className="w-4 h-4 mr-2" />
                    {guild.name}
                    <span className="ml-auto text-xs text-muted-foreground">
                      {guild.member_count} members
                    </span>
                  </DropdownMenuItem>
                ))
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onManageGuilds}>
                <Plus className="w-4 h-4 mr-2" />
                Create / Join Guild
              </DropdownMenuItem>
              {guilds.length > 0 && (
                <>
                  <DropdownMenuItem onClick={onManageGuilds}>
                    <Settings className="w-4 h-4 mr-2" />
                    Manage Guilds
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/guild-stats")}>
                    <TrendingUp className="w-4 h-4 mr-2" />
                    Guild Statistics
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Compact mode toggle (laptop/tablet density) */}
          {onToggleCompact && (
            <Button
              variant={compactMode ? "default" : "ghost"}
              size="sm"
              onClick={onToggleCompact}
              className="gap-1.5 hidden md:inline-flex"
              title={compactMode ? "Switch back to comfortable layout" : "Compact mode — fit more bosses on small laptop screens"}
              data-testid="compact-mode-toggle"
            >
              <Layout className="w-4 h-4" />
              <span className="hidden lg:inline">{compactMode ? "Compact" : "Compact"}</span>
            </Button>
          )}

          {/* Rankings Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/rankings")}
            className="gap-2 text-yellow-300 hover:text-yellow-200 hover:bg-yellow-500/10"
            title="Rankings - Top hunters by kill count"
            data-testid="rankings-nav-btn"
          >
            <Trophy className="w-4 h-4" />
            <span className="hidden sm:inline">Rankings</span>
          </Button>

          {/* Hunter's Chance Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/hunters-chance")}
            className="gap-2 text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
            title="Hunter's Chance - Bounties & Expeditions"
          >
            <img src="/hc-logo.png" alt="HC" style={{ width: "24px", height: "14px", objectFit: "contain", filter: "drop-shadow(0 0 3px rgba(255,215,0,0.5))" }} />
            <span className="hidden sm:inline">Hunter's Chance</span>
          </Button>

          {/* Ore Store Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/orestore")}
            className="gap-2 text-orange-300 hover:text-orange-200 hover:bg-orange-500/10"
            title="Ore Store - Sell your ores, plates and crystals"
          >
            <img src="https://customer-assets.emergentagent.com/job_refinery-hub-4/artifacts/3azwctos_Ore-Store-LOGO2.png" alt="OreStore" style={{ height: "22px", width: "auto", objectFit: "contain", filter: "drop-shadow(0 0 3px rgba(255,165,0,0.6))" }} />
            <span className="hidden sm:inline">Ore Store</span>
          </Button>

          {/* Notification Bell - Only show when logged in */}
          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="relative"
                  title={`${notifications.total} notification${notifications.total !== 1 ? 's' : ''}`}
                  data-testid="notifications-bell"
                >
                  <Bell className={cn("w-4 h-4", notifications.total > 0 && "text-amber-400")} />
                  {notifications.total > 0 && (
                    <span className="absolute -top-1 -right-1 bg-amber-500 text-[10px] text-white rounded-full min-w-[16px] h-4 flex items-center justify-center font-bold px-1">
                      {notifications.total > 99 ? "99+" : notifications.total}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80 max-h-[400px] overflow-y-auto">
                <div className="flex items-center justify-between px-2">
                  <DropdownMenuLabel>Notifications</DropdownMenuLabel>
                  {notifications.total > 0 && notifications.hunter_chance_notifications?.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        clearAllHunterChanceNotifications();
                      }}
                    >
                      <Trash2 className="w-3 h-3 mr-1" />
                      Clear All
                    </Button>
                  )}
                </div>
                <DropdownMenuSeparator />
                {notifications.total === 0 ? (
                  <div className="text-sm text-muted-foreground text-center py-4">
                    No notifications
                  </div>
                ) : (
                  <>
                    {notifications.pending_friend_requests > 0 && (
                      <DropdownMenuItem onClick={() => navigate("/profile")} className="cursor-pointer">
                        <UserPlus className="w-4 h-4 mr-2 text-blue-400 flex-shrink-0" />
                        <div className="flex-1">
                          <span className="font-medium">{notifications.pending_friend_requests} Friend Request{notifications.pending_friend_requests !== 1 ? 's' : ''}</span>
                          <p className="text-xs text-muted-foreground">Someone wants to be your friend!</p>
                        </div>
                      </DropdownMenuItem>
                    )}
                    {notifications.pending_invitations > 0 && (
                      <DropdownMenuItem onClick={onManageGuilds} className="cursor-pointer">
                        <Users className="w-4 h-4 mr-2 text-emerald-400 flex-shrink-0" />
                        <div className="flex-1">
                          <span className="font-medium">{notifications.pending_invitations} Guild Invitation{notifications.pending_invitations !== 1 ? 's' : ''}</span>
                          <p className="text-xs text-muted-foreground">You've been invited to join guilds</p>
                        </div>
                      </DropdownMenuItem>
                    )}
                    {notifications.pending_applications > 0 && (
                      <DropdownMenuItem onClick={onManageGuilds} className="cursor-pointer">
                        <Shield className="w-4 h-4 mr-2 text-amber-400 flex-shrink-0" />
                        <div className="flex-1">
                          <span className="font-medium">{notifications.pending_applications} Pending Application{notifications.pending_applications !== 1 ? 's' : ''}</span>
                          <p className="text-xs text-muted-foreground">Players want to join your guild</p>
                        </div>
                      </DropdownMenuItem>
                    )}
                    
                    {/* Hunter's Chance Notifications - Show each one individually */}
                    {notifications.hunter_chance_notifications?.length > 0 && (
                      <>
                        <DropdownMenuSeparator />
                        <div className="px-2 py-1 text-xs text-muted-foreground font-medium flex items-center gap-1">
                          <Sword className="w-3 h-3" />
                          Hunter's Chance Updates
                        </div>
                        {notifications.hunter_chance_notifications.map((notif) => (
                          <div 
                            key={notif.id} 
                            className="group relative flex items-start gap-2 px-2 py-2 hover:bg-accent/50 cursor-pointer rounded-sm"
                            onClick={() => {
                              // For admin deleted notifications, just clear it - don't navigate
                              if (notif.type === "admin_bounty_deleted" || notif.type === "admin_expedition_deleted") {
                                clearSingleNotification(notif.id, { stopPropagation: () => {} });
                              } else if (notif.bounty_id) {
                                navigate(`/hunters-chance/bounty/${notif.bounty_id}`);
                              } else if (notif.expedition_id) {
                                navigate(`/hunters-chance/expedition/${notif.expedition_id}`);
                              } else {
                                navigate("/hunters-chance");
                              }
                            }}
                          >
                            <Target className={cn(
                              "w-4 h-4 mt-0.5 flex-shrink-0",
                              notif.type?.includes("bounty") ? "text-amber-400" : "text-indigo-400"
                            )} />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm leading-tight">{getNotificationDescription(notif)}</p>
                              <p className="text-xs text-muted-foreground mt-0.5">{notif.boss_name}</p>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-red-400 hover:bg-red-500/10 flex-shrink-0"
                              onClick={(e) => clearSingleNotification(notif.id, e)}
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                      </>
                    )}
                    
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* User Section */}
        <div className="flex items-center gap-2">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2" data-testid="user-menu-button">
                  {user.avatar ? (
                    <img 
                      src={user.avatar} 
                      alt={user.username} 
                      className="w-6 h-6 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-6 h-6 bg-primary/20 rounded-full flex items-center justify-center">
                      <span className="text-xs font-bold text-primary">
                        {user.username.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <div className="hidden sm:flex flex-col items-start">
                    <span className="text-sm">{user.username}</span>
                    {user.main_guild && (
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        {user.main_guild.emblem && (
                          <img 
                            src={user.main_guild.emblem} 
                            alt="" 
                            className="w-3 h-3 rounded-sm object-cover"
                          />
                        )}
                        {user.main_guild.name}
                      </span>
                    )}
                  </div>
                  <ChevronDown className="w-3 h-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>
                  <div>
                    <div>{user.email}</div>
                    {user.main_guild && (
                      <div className="text-xs font-normal text-muted-foreground flex items-center gap-1 mt-1">
                        <Users className="w-3 h-3" />
                        {user.main_guild.name}
                      </div>
                    )}
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/profile")}>
                  <UserCircle className="w-4 h-4 mr-2" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/stats")}>
                  <BarChart3 className="w-4 h-4 mr-2" />
                  My Statistics
                </DropdownMenuItem>
                {guilds.length > 0 && (
                  <DropdownMenuItem onClick={() => navigate("/guild-stats")}>
                    <TrendingUp className="w-4 h-4 mr-2" />
                    Guild Statistics
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={onManageGuilds}>
                  <Users className="w-4 h-4 mr-2" />
                  Manage Guilds
                </DropdownMenuItem>
                {(user.is_admin || user.is_junior_admin) && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate("/admin")}>
                      <Shield className="w-4 h-4 mr-2" />
                      Admin Panel
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="text-destructive">
                  <LogIn className="w-4 h-4 mr-2 rotate-180" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={onLogin}
              className="gap-2"
              data-testid="login-button"
            >
              <LogIn className="w-4 h-4" />
              <span className="hidden sm:inline">Login</span>
            </Button>
          )}
          
          {/* Bug Report Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowBugReport(true)}
            className="gap-2 text-muted-foreground hover:text-red-400"
            title="Report a Bug"
          >
            <Bug className="w-4 h-4" />
            <span className="hidden sm:inline">Report Bug</span>
          </Button>
        </div>
      </div>
      
      {/* Bug Report Modal */}
      <BugReportModal
        open={showBugReport}
        onOpenChange={setShowBugReport}
        user={user}
      />
    </div>
  );
};

export default TrackerTabs;
